import React, { useEffect, useState } from 'react';
import { UserPlus, Building2, MapPin, Briefcase, Loader2, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FLAG_STYLES = {
  'Major New Employer': 'bg-purple-100 text-purple-700 border-purple-200',
  'Active New Employer': 'bg-blue-100 text-blue-700 border-blue-200',
  'New Hiring Organization': 'bg-teal-100 text-teal-700 border-teal-200',
};

const NewCompanies = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('jobs_7d');
  const [sortDir, setSortDir] = useState('desc');
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/new-companies?limit=200${filterParams}`)
      .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [excludeStaffing, excludeRetail]);

  const handleSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(f); setSortDir('desc'); }
  };

  const companies = data?.companies || [];
  const sorted = [...companies].sort((a, b) => {
    const m = sortDir === 'desc' ? -1 : 1;
    return m * ((a[sortField] || 0) - (b[sortField] || 0));
  });

  const flagDist = data?.flag_distribution || {};

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-4 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap text-xs`}
      onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">{label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <IntelligenceLayout title="New Companies" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-teal-50 rounded-xl flex items-center justify-center">
            <UserPlus className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="new-companies-title">New Companies Hiring</h1>
            <p className="text-sm text-gray-500">Organizations appearing in the hiring market for the first time</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing}
          excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Scanning for new companies...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="new-companies-metrics">
              <MetricCard label="New This Week" value={data?.total_new_7d || 0} icon={UserPlus} />
              <MetricCard label="New This Month" value={data?.total_new_30d || 0} icon={Building2} />
              <MetricCard label="Major Employers" value={flagDist['Major New Employer'] || 0} icon={Star} />
              <MetricCard label="Active New" value={flagDist['Active New Employer'] || 0} icon={Briefcase} />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="new-companies-table">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <h3 className="text-sm font-semibold text-[#0A1F44]">New Companies Detected</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/30">
                      <th className="text-left px-4 py-3 w-8">#</th>
                      <th className="text-left px-4 py-3">Company</th>
                      <SortHeader field="jobs_7d" label="Jobs (7d)" align="right" />
                      <SortHeader field="cities_count" label="Cities" align="right" />
                      <SortHeader field="states_count" label="States" align="right" />
                      <th className="text-left px-4 py-3">Industry</th>
                      <th className="text-left px-4 py-3">Top Roles</th>
                      <th className="text-left px-4 py-3">Locations</th>
                      <th className="text-center px-4 py-3">Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((item, i) => (
                      <tr key={item.company} className="border-t border-gray-100 hover:bg-[#f8f6f0] transition-colors" data-testid={`new-company-row-${i}`}>
                        <td className="px-4 py-3 text-xs text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3"><div className="flex items-center gap-2"><WatchlistButton company={item.company} sourceModule="new-companies" /><span className="font-semibold text-[#0A1F44]">{item.company}</span></div></td>
                        <td className="px-4 py-3 text-right font-bold text-teal-700">{item.jobs_7d}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{item.cities_count}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{item.states_count}</td>
                        <td className="px-4 py-3">{item.industry ? <div className="flex flex-col gap-1"><span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200 w-fit">{item.industry}</span><CompanyTypeBadge type={item.company_type} /></div> : '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{(item.top_roles || []).slice(0, 2).join(', ') || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="h-3 w-3 text-gray-400" />
                            {(item.top_locations || []).slice(0, 2).join('; ') || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${FLAG_STYLES[item.flag] || FLAG_STYLES['New Hiring Organization']}`}>{item.flag}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default NewCompanies;
